/**
 * Estro zoom lightbox — Modivo-like fullscreen gallery.
 *
 * Two linked Swipers:
 *   - main swiper (one image at a time) with the Zoom module
 *     (click/tap toggles zoom, mouse move pans on desktop, pinch on mobile)
 *   - vertical thumbnails swiper on the left, linked via `thumbs`
 *
 * Opened by estro-gallery via `open(index, event)`. Swiper is expected to be
 * available as the global `window.Swiper` (loaded by swiper-bundle.min.js).
 */

const MAX_RATIO = 2.5;

class EstroZoomDialog extends HTMLElement {
  /** @type {any} */
  #main = null;
  /** @type {any} */
  #thumbs = null;
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {boolean} */
  #initialized = false;
  /** @type {HTMLElement | null} */
  #zoomedImage = null;
  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-ref="dialog"]'));

    const closeButton = this.querySelector('[data-ref="close"]');
    closeButton?.addEventListener('click', () => this.close(), { signal });

    // Close when clicking the backdrop / empty area (native dialog).
    this.#dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.#dialog) this.close();
      },
      { signal }
    );

    // Reset zoom when closed via Escape or programmatically.
    this.#dialog?.addEventListener(
      'close',
      () => {
        this.#resetZoom();
        document.body.style.overflow = '';
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#main?.destroy(true, true);
    this.#thumbs?.destroy(true, true);
  }

  /**
   * Opens the lightbox at the given media index.
   * @param {number} [index]
   * @param {Event} [event]
   */
  open(index = 0, event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (!this.#dialog) return;

    this.#dialog.showModal();
    document.body.style.overflow = 'hidden';

    if (this.#initSwipers()) {
      this.#goTo(index);
    } else {
      // Swiper not loaded yet — wait for it, then init while dialog is visible.
      this.#waitForSwiper(() => {
        if (this.#initSwipers()) this.#goTo(index);
      });
    }
  }

  /**
   * Closes the lightbox.
   */
  close() {
    this.#resetZoom();
    this.#dialog?.close();
  }

  /**
   * Initializes the main + thumbnail Swipers (once).
   * @returns {boolean} Whether Swiper is ready and instances exist.
   */
  #initSwipers() {
    if (this.#initialized) return true;

    const SwiperClass = /** @type {any} */ (window).Swiper;
    if (!SwiperClass) return false;

    const mainEl = this.querySelector('[data-ref="main"]');
    if (!mainEl) return false;

    const thumbsEl = this.querySelector('[data-ref="thumbs"]');
    if (thumbsEl) {
      this.#thumbs = new SwiperClass(thumbsEl, {
        direction: 'vertical',
        slidesPerView: 'auto',
        freeMode: true,
        watchSlidesProgress: true,
        spaceBetween: 8,
      });
    }

    this.#main = new SwiperClass(mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      keyboard: { enabled: true },
      navigation: {
        nextEl: this.querySelector('[data-ref="next"]'),
        prevEl: this.querySelector('[data-ref="prev"]'),
      },
      thumbs: this.#thumbs ? { swiper: this.#thumbs } : undefined,
      observer: true,
      observeParents: true,
    });

    // Custom zoom (we don't use Swiper's Zoom module — it grew the box and
    // required a drag to pan). Single click toggles zoom at the pointer; while
    // zoomed, moving the mouse pans automatically (no click). Modivo behaviour.
    const { signal } = this.#controller;
    mainEl.addEventListener('click', /** @type {EventListener} */ (this.#handleZoomClick), { signal });
    mainEl.addEventListener('mousemove', /** @type {EventListener} */ (this.#handleZoomMove), { signal });
    this.#main.on('slideChange', () => {
      this.#resetZoom();
      this.#updateThumbNav();
    });

    // The up/down chevrons next to the thumbnails navigate the MAIN image
    // (prev/next) — like Modivo — and get a disabled state at the ends.
    this.querySelector('[data-ref="thumbPrev"]')?.addEventListener('click', () => this.#main?.slidePrev(), { signal });
    this.querySelector('[data-ref="thumbNext"]')?.addEventListener('click', () => this.#main?.slideNext(), { signal });

    this.#updateThumbNav();

    this.#initialized = true;
    return true;
  }

  /**
   * Disables the up/down thumbnail arrows at the first / last image.
   */
  #updateThumbNav() {
    const prev = this.querySelector('[data-ref="thumbPrev"]');
    const next = this.querySelector('[data-ref="thumbNext"]');
    const atStart = this.#main?.isBeginning ?? true;
    const atEnd = this.#main?.isEnd ?? true;

    prev?.classList.toggle('is-disabled', atStart);
    if (prev instanceof HTMLButtonElement) prev.disabled = atStart;
    next?.classList.toggle('is-disabled', atEnd);
    if (next instanceof HTMLButtonElement) next.disabled = atEnd;
  }

  /**
   * Sizes the thumbnail swiper to its content (capped to the available height)
   * so the thumbs + arrows form one compact, vertically centered group.
   */
  #sizeThumbs() {
    const col = /** @type {HTMLElement | null} */ (this.querySelector('.estro-zoom__thumbs-col'));
    const thumbsEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-ref="thumbs"]'));
    if (!col || !thumbsEl) return;

    const slides = thumbsEl.querySelectorAll('.swiper-slide');
    if (!slides.length) return;

    const thumbHeight = 85;
    const gap = 8;
    const contentHeight = slides.length * thumbHeight + (slides.length - 1) * gap;

    // Reserve room for the two arrows (36px each) and their gaps.
    const available = col.clientHeight - 88;
    thumbsEl.style.height = `${Math.max(0, Math.min(contentHeight, available))}px`;

    this.#thumbs?.update();
  }

  /**
   * Toggle zoom on click.
   * @param {MouseEvent} event
   */
  #handleZoomClick = (event) => {
    // Mobile / touch: no click-zoom — the lightbox is a plain swipeable carousel.
    if (this.#isTouch()) return;

    const target = /** @type {Element | null} */ (event.target);
    const container = target?.closest('.swiper-zoom-container');
    if (!container) return;

    const image = /** @type {HTMLElement | null} */ (container.querySelector('.estro-zoom__image'));
    if (!image) return;

    if (this.#zoomedImage === image) {
      this.#resetZoom();
    } else {
      this.#resetZoom();
      this.#zoomedImage = image;
      container.classList.add('is-zoomed');
      // Animate the zoom-in; panning (mousemove) switches to instant below.
      image.style.setProperty('--ez-transition', 'transform 0.18s ease');
      if (this.#main) this.#main.allowTouchMove = false;
      this.#panToPointer(event);
    }
  };

  /**
   * Pan the zoomed image following the cursor (no click needed).
   * @param {MouseEvent} event
   */
  #handleZoomMove = (event) => {
    if (!this.#zoomedImage) return;
    // Instant follow — no trailing while moving the mouse.
    this.#zoomedImage.style.setProperty('--ez-transition', 'none');
    this.#panToPointer(event);
  };

  /**
   * Whether we're on a touch / small-screen device (no click-zoom there).
   * @returns {boolean}
   */
  #isTouch() {
    return window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
  }

  /**
   * Applies scale + translate so the point under the cursor is revealed.
   * @param {MouseEvent} event
   */
  #panToPointer(event) {
    const image = this.#zoomedImage;
    if (!image) return;

    const box = image.closest('.swiper-zoom-container') || image.parentElement;
    if (!box) return;

    const rect = box.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const clamp = (/** @type {number} */ v) => Math.min(1, Math.max(0, v));
    const relX = clamp((event.clientX - rect.left) / rect.width);
    const relY = clamp((event.clientY - rect.top) / rect.height);

    const scale = MAX_RATIO;
    // Max translate (pre-scale units) so the image edges stay within the box.
    const maxTx = (rect.width * (scale - 1)) / 2 / scale;
    const maxTy = (rect.height * (scale - 1)) / 2 / scale;
    const tx = (0.5 - relX) * 2 * maxTx;
    const ty = (0.5 - relY) * 2 * maxTy;

    image.style.transform = `scale(${scale}) translate(${tx}px, ${ty}px)`;
  }

  /**
   * Resets any active zoom.
   */
  #resetZoom() {
    if (this.#zoomedImage) {
      // Animate the zoom-out.
      this.#zoomedImage.style.setProperty('--ez-transition', 'transform 0.18s ease');
      this.#zoomedImage.style.transform = '';
      this.#zoomedImage.closest('.swiper-zoom-container')?.classList.remove('is-zoomed');
      this.#zoomedImage = null;
    }
    if (this.#main) this.#main.allowTouchMove = true;
  }

  /**
   * Jumps to the given index and resets zoom.
   * @param {number} index
   */
  #goTo(index) {
    requestAnimationFrame(() => {
      this.#main?.update();
      this.#thumbs?.update();
      this.#sizeThumbs();
      this.#resetZoom();
      this.#main?.slideTo(index, 0);
      this.#updateThumbNav();
    });
  }

  /**
   * @param {() => void} callback
   */
  #waitForSwiper(callback) {
    if (/** @type {any} */ (window).Swiper) return callback();

    const { signal } = this.#controller;
    const script = document.querySelector('script[src*="swiper-bundle"]');
    script?.addEventListener('load', () => callback(), { once: true, signal });

    // Fallback poll in case the load event was missed.
    let tries = 0;
    const poll = () => {
      if (/** @type {any} */ (window).Swiper) return callback();
      if (tries++ > 120) return;
      requestAnimationFrame(poll);
    };
    poll();
  }
}

if (!customElements.get('estro-zoom-dialog')) {
  customElements.define('estro-zoom-dialog', EstroZoomDialog);
}
